/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import { formatNumber } from 'accounting';
import Big from 'big.js';
import { paymentsInitialValues } from 'components/Molecules/Drawdowns/CreateDrawdownDrawer/formData';
import { FormikPaymentType, FormikType } from 'components/Molecules/Payments/CreatePaymentDrawer/formData';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import useAuthorization from 'hooks/useAuthorization';
import useLog from 'hooks/useLog';
import { t } from 'i18next';
import { UserRole } from 'models/user';
import moment, { Moment } from 'moment';
import { useMutation, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import instance from 'services/Axios/instance';
import CurrencyAccountsService from 'services/CurrencyAccounts/currencyAccounts.service';
import FXTradeService from 'services/FXTrade/fxTrade.service';
import { TStore } from 'store';
import { initiateDrawdown, initiatePayment } from 'store/authy/actions';
import { TAuthyState } from 'store/authy/reducer';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import {
  DrawdownFundingMethod, DrawdownSubmissionRequest,
  MassDrawdownRequest,
  MassDrawdownValidationDto,
} from '@alpha/fx-dtos';
import { TradeDto } from '@alpha/fx-dtos/lib/responses/TradeDto';

import useForm from '../../../../hooks/useForm';

import {
  drawdownSubmissionInitialValues,
  formValidation,
  initialValues,
  MassDrawdownRequestForm,
  submissionformValidation,
} from './formData';
import { Section } from './NewMassDrawdownDrawer';

export const useMassDrawdown = () => {
  const { logEvent, logError } = useLog();
  const [massDrawdownDrawerOpen, setMassDrawdownDrawerOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [isMassDrawdownFlowActive, setIsMassDrawdownFlowActive] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState<TradeDto[]>([]);
  const [selectedTradeIds, setSelectedTradeIds] = useState<string[]>([]);
  const [availableForwardTrades, setAvailableForwardTrades] = useState<TradeDto[]>([]);
  const [additionalTrades, setAdditionalTrades] = useState<TradeDto[]>([]);
  const [massDrawdownValueDate, setMassDrawdownValueDate] = useState<
    string | null
  >(null);
  const [trades, setTrades] = useState<TradeDto[]>([]); // For looking up when selecting trades
  const [validatedDrawdownsResponse, setValidatedDrawdownsResponse] = useState<MassDrawdownValidationDto>();
  const [paymentFormValidation, setPaymentFormValidation] = useState(formValidation);
  const { authorized: authorizedPaymentApproverOwn } = useAuthorization(
    [[UserRole.PAYMENTS_APPROVER_OWN]],
  );
  const dispatch = useDispatch();
  const [padFundingMethods, setPadFundingMethods] = useState<boolean>(false);
  const [section, setSection] = useState<Section>('Drawdown');
  const [confirmationModalType, setConfirmationModalType] = useState<'CLEAR' | 'CLOSE' | 'REMOVE' | 'BACK_TO_DRAWDOWN'>('CLEAR');
  const [tradeIdToRemove, setTradeIdToRemove] = useState<string>('');
  const [padEligibleAndApproved, setPadEligibleAndApproved] = useState<boolean>(false);
  const sb = useAlphaSnackbar();
  const [defaultSellCurrencyAccount,
    setDefaultSellCurrencyAccount] = useState<CurrencyAccountDto>();
  const [defaultBuyCurrencyAccount,
    setDefaultBuyCurrencyAccount] = useState<CurrencyAccountDto>();
  const [calculatedWeightedRate, setCalculatedWeightedRate] = useState<number>(0);
  const [tradeErrors, setTradeErrors] = useState<Record<string, string>>({});

  const requestForm = useForm<MassDrawdownRequestForm>(
    initialValues,
    formValidation,
    (validateForm: MassDrawdownRequestForm) => {
      if (!defaultBuyCurrencyAccount) {
        sb.trigger(`${t('You_do_not_have_a_default_currency_account1')} ${validateForm.buyCurrencyCode} ${t('You_do_not_have_a_default_currency_account2')}.`, 'error');
        logError({ action: 'No currency account', detail: { name: validateForm.buyCurrencyCode } });
        return;
      }

      if (requestForm.values.fundingMethod === DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE
        && !defaultSellCurrencyAccount) {
        sb.trigger(`${t('You_do_not_have_a_default_currency_account3')} ${validateForm.sellCurrencyCode} ${t('currency_account_to_fund_your_drawdown')}.`, 'error');
        logError({ action: 'No currency account', detail: { name: validateForm.sellCurrencyCode } });
        return;
      }

      validateDrawdownMutations.mutate({
        validateTrades: selectedTrades,
        validateForm: { ...validateForm, weightedRate: Number(formatNumber(calculatedWeightedRate || 0, 4)), valueDate: validateForm.valueDate ?? '' },
      });
    }, true,
  );

  const handleMassDrawdownClick = () => {
    logEvent({ action: 'Click New Mass Drawdown' });
    setIsMassDrawdownFlowActive(true);
  };

  const handleCancelMassDrawdown = () => {
    setIsMassDrawdownFlowActive(false);
    setSelectedTrades([]);
    setSelectedTradeIds([]);
  };

  const handleValueDateChange = (_date: Moment) => {
    if (_date) {
      requestForm.setFieldValue('valueDate', _date.format('YYYY-MM-DD'));
    }
  };

  const handleSplitChange = (
    e: React.ChangeEvent<{ value: string | unknown }>,
  ): void => {
    // don't use tradeId, use index
    if (e.target.value) requestForm.setFieldValue('splitType', String(e.target.value));
  };

  const handleCurrencyChange = (
    e: React.ChangeEvent<{ value: string | unknown }>,
  ): void => {
    requestForm.setFieldValue('fixCurrencyCode', String(e.target.value));
    requestForm.values.drawdowns.forEach((item, index) => {
      requestForm.setFieldValue(`drawdowns[${index}].fixCurrencyCode`, String(e.target.value));
      if (requestForm.values?.splitType === 'split-evenly' && !requestForm.values.evenSplitAmount) return;
      validateDrawdownAmount(item.tradeId, index, item.amount, String(e.target.value));
    });
  };

  const validateMinimumAmount = (
    inputValue: number, fixCurrencyCode: string, trade: TradeDto,
  ): boolean => {
    const minFX = accountConfigQuery.data?.minimumFX;
    const isEmptyEvenSplitAmount = requestForm.values?.splitType === 'split-evenly'
      && (Number.isNaN(requestForm.values.evenSplitAmount) || requestForm.values.evenSplitAmount === null);

    if (trade && minFX && !isEmptyEvenSplitAmount) {
      if ((fixCurrencyCode === trade?.buyCurrencyCode && inputValue < minFX?.buyAmount)
        || (fixCurrencyCode === trade?.soldCurrencyCode && inputValue < minFX?.sellAmount)) {
        setTradeErrors((prev) => ({ ...prev, [trade.id]: t('amount_is_below_the_minimum_drawdown_limit') }));
        return false;
      }
    }
    return true;
  };

  const validateMaximumAmount = (
    inputValue: number, fixedCurrency: string, trade: TradeDto,
  ): boolean => {
    const maxFX = accountConfigQuery.data?.maximumFX;
    const aboveFXLimitBuy = (maxFX?.buyAmount && inputValue > maxFX?.buyAmount);
    const aboveFXLimitSell = (maxFX?.sellAmount && inputValue > maxFX?.sellAmount);

    if (trade) {
      if (maxFX && ((fixedCurrency === trade.buyCurrencyCode && aboveFXLimitBuy)
        || (fixedCurrency === trade.soldCurrencyCode && aboveFXLimitSell))) {
        setTradeErrors((prev) => ({ ...prev, [trade.id]: t('amount_is_above_the_maximum_drawdown_limit') }));
        return false;
      }
      if ((fixedCurrency === trade.buyCurrencyCode && inputValue > trade.buyBalance)
        || (fixedCurrency === trade.soldCurrencyCode && inputValue > trade.sellBalance)
      ) {
        setTradeErrors((prev) => ({ ...prev, [trade.id]: t('this_trade_does_not_have_sufficient_balance') }));
        return false;
      }
    }
    return true;
  };

  const validateDrawdownAmount = (tradeId: string, index: number, amount: number | null, fixCurrencyCode: string) => {
    if (amount === null) return;
    const isValidMaximumAmount = validateMaximumAmount(
      amount,
      fixCurrencyCode,
      selectedTrades[index],
    );
    const isValidMinimumAmount = validateMinimumAmount(
      amount,
      fixCurrencyCode,
      selectedTrades[index],
    );
    if (!(isValidMinimumAmount && isValidMaximumAmount)) {
      requestForm.setFieldTouched(`drawdowns[${index}].amount`, true, false);
    } else {
      requestForm.setFieldTouched(`drawdowns[${index}].amount`, true, true);
      setTradeErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[tradeId];
        return newErrors;
      });
    }
  };

  const handleAmountChange = (amount: number, trade: TradeDto) => {
    if (requestForm.values?.splitType === 'split-evenly') {
      requestForm.setFieldValue('evenSplitAmount', amount, true);
      requestForm.setFieldTouched('evenSplitAmount', true, true);
    } else {
      const index = requestForm.values.drawdowns.findIndex((item) => item.tradeId === trade.id);
      // create copy array and calculate total with copy
      const drawdownValues = [...requestForm.values.drawdowns];
      drawdownValues[index] = { ...drawdownValues[index], amount };
      const newTotal = drawdownValues.reduce((acc, curr) => acc.plus(curr.amount || 0), new Big(0)).toNumber();
      requestForm.setValues({
        ...requestForm.values,
        drawdowns: drawdownValues,
        totalDrawdownAmount: newTotal,
      }, true);
      validateDrawdownAmount(trade.id, index, amount, requestForm.values.fixCurrencyCode);
    }
  };

  const handleRemoveTrade = (tradeId: string) => {
    setSelectedTradeIds(selectedTradeIds.filter((id) => id !== tradeId));
    setSelectedTrades(selectedTrades.filter((trade) => trade.id !== tradeId));
    setTradeErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[tradeId];
      return newErrors;
    });
  };

  const getAvailableForwardTrades = async (): Promise<TradeDto[]> => {
    const filteredTrades = await FXTradeService.getAvailableForwardTrades({
      valueDateFrom: moment(massDrawdownValueDate).format('YYYY-MM-DD'),
      buyCurrencyCode: selectedTrades[0].buyCurrencyCode,
      soldCurrencyCode: selectedTrades[0].soldCurrencyCode,
    });
    return filteredTrades.records.filter((trade) => trade.drawdownAvailable);
  };

  const handleAddAdditionalTrade = (tradeId: string) => {
    setSelectedTradeIds([...selectedTradeIds, tradeId]);
    setSelectedTrades([...selectedTrades, availableForwardTrades.find((trade) => trade.id === tradeId) as TradeDto]);
  };

  const accountConfigQuery = useQuery('getAccountConfig', () => FXTradeService.getAccountConfigurationWithCcys(
    requestForm.values.sellCurrencyCode ?? '',
    requestForm.values.buyCurrencyCode ?? '',
  ), {
    enabled: false,
  });

  if (accountConfigQuery.error) {
    sb.trigger(`${t('error_loading_account_configurations')}.`);
    logError({ action: 'Error loading account configurations.' });
  }

  const getDefaultCurrencyAccounts = async (soldCurrencyCode: string, buyCurrencyCode: string) => {
    const soldCA = await getDefaultCurrencyAccount(soldCurrencyCode);
    const buyCA = await getDefaultCurrencyAccount(buyCurrencyCode);
    setDefaultSellCurrencyAccount(soldCA);
    setDefaultBuyCurrencyAccount(buyCA);
  };

  const getDefaultCurrencyAccount = async (currencyCode: string) => {
    try {
      const currencyAccounts = await CurrencyAccountsService.getCurrencyStats(
        currencyCode,
      );

      const defaultCA = currencyAccounts.currencyAccounts?.find(
        (ca) => ca.default,
      );

      return defaultCA;
    } catch (e) {
      return undefined;
    }
  };

  useEffect(() => {
    if (massDrawdownDrawerOpen && requestForm.values.sellCurrencyCode && requestForm.values.buyCurrencyCode) {
      getDefaultCurrencyAccounts(requestForm.values.sellCurrencyCode, requestForm.values.buyCurrencyCode);
      accountConfigQuery.refetch().catch((error) => {
        sb.trigger(error?.message || `${t('error_loading_account_configurations')}.`);
        logError({ action: 'Error loading account configurations.', error });
      });
    }
  }, [massDrawdownDrawerOpen, requestForm.values.sellCurrencyCode, requestForm.values.buyCurrencyCode]);

  useEffect(() => {
    if (massDrawdownDrawerOpen && requestForm.values?.buyCurrencyCode && requestForm.values?.sellCurrencyCode) {
      if (accountConfigQuery) accountConfigQuery.refetch();
    }
  }, [massDrawdownDrawerOpen, requestForm.values.buyCurrencyCode, requestForm.values.sellCurrencyCode]);

  const handleFormSubmit = () => {
    requestForm.submitForm();
  };

  const handleConfirmationModal = () => {
    setOpenConfirmation(false);
    switch (confirmationModalType) {
      case 'CLOSE':
        requestForm.resetForm();
        setTradeErrors({});
        setMassDrawdownDrawerOpen(false);
        paymentsForm.setFieldValue('payments', []);
        break;
      case 'CLEAR':
        requestForm.resetForm();
        setTradeErrors({});
        paymentsForm.setFieldValue('payments', []);
        sb.trigger(t('cleared_all_input'), 'info');
        break;
      case 'REMOVE':
        handleRemoveTrade(tradeIdToRemove);
        break;
      case 'BACK_TO_DRAWDOWN':
        handleSetSection('Drawdown');
        paymentsForm.setFieldValue('payments', []);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isMassDrawdownFlowActive) {
      setMassDrawdownValueDate(new Date().toISOString());
    }
  }, [isMassDrawdownFlowActive]);

  const mapTradeObjectWithSelectedIds = (
    newSelectedTradeIds: string[],
  ): void => {
    const resultTrades: TradeDto[] = selectedTrades?.filter((trade) => newSelectedTradeIds?.includes(trade.id)) || [];

    const multiSelectedTradesIds = selectedTrades?.map((trade) => trade.id);
    const newlySelectedIds = newSelectedTradeIds?.filter(
      (tId) => !multiSelectedTradesIds?.includes(tId),
    );

    const newlySelected: TradeDto[] = trades?.filter((item) => newlySelectedIds.includes(item.id)) || [];

    resultTrades.push(...newlySelected);

    setSelectedTrades(resultTrades);
  };

  const validateDrawdowns = async ({
    validateTrades, validateForm,
  }: {
    validateTrades: TradeDto[],
    validateForm: MassDrawdownRequestForm,
  }) => {
    const requestData = {
      currencyPair: validateForm.currencyPair,
      totalDrawdownAmount: validateForm.totalDrawdownAmount,
      weightedRate: validateForm.weightedRate,
      valueDate: validateForm.valueDate,
      sellCurrencyCode: validateForm.sellCurrencyCode,
      buyCurrencyCode: validateForm.buyCurrencyCode,
      fixCurrencyCode: validateForm.fixCurrencyCode,
      fundingMethod: validateForm.fundingMethod,
      drawdowns: validateForm.drawdowns?.map((form) => {
        const matchingTrade = validateTrades.find((trade) => trade.id === form.tradeId);
        return {
          tradeId: matchingTrade ? matchingTrade.id : '',
          fixCurrencyCode: validateForm.fixCurrencyCode,
          amount: form.amount,
          valueDate: validateForm.valueDate,
          fundingMethod: validateForm.fundingMethod,
          sellCurrencyCode: matchingTrade ? matchingTrade.soldCurrencyCode : '',
          buyCurrencyCode: matchingTrade ? matchingTrade.buyCurrencyCode : '',
          rate: matchingTrade ? matchingTrade.rate : '',
          massDrawdownId: form.massDrawdownId,
        };
      }),
    } as MassDrawdownRequest;

    const validateResult = await FXTradeService.postValidateDrawdownBatch(requestData);
    return validateResult;
  };

  const validateDrawdownMutations = useMutation(validateDrawdowns, {
    onSuccess: (data: MassDrawdownValidationDto) => {
      const allErrors = data.validatedDrawdowns?.some((item) => item.errors && item.errors.length > 0);

      if (allErrors) {
        const firstErrorItem = data.validatedDrawdowns?.find((item) => item.errors && item.errors.length > 0);
        const errorMessage = firstErrorItem?.errors[0] || t('error_in_validating_drawdown');
        sb.trigger(`${t('validation_error')} ${errorMessage}`);
        const errorMap = data.validatedDrawdowns?.reduce((acc, item) => {
          acc[item?.tradeId as string] = item.errors?.[0] || t('error_in_validating_drawdown');
          return acc;
        }, {} as Record<string, string>);
        setTradeErrors((prev) => ({ ...prev, ...errorMap }));
      } else {
        setValidatedDrawdownsResponse(data);
        sb.trigger(t('drawdown_validated'), 'success');
        handleSetSection('Settlement');
      }
    },
    onError: (error) => {
      sb.trigger(`${t('error_in_validating_drawdown')}: ${error}`, 'error');
      logError({ action: 'Error Validating Drawdowns', error });
    },
  });

  const drawdownSubmissionForm = useForm<DrawdownSubmissionRequest>(
    drawdownSubmissionInitialValues,
    submissionformValidation, (submissionForm: DrawdownSubmissionRequest) => {
      if (validatedDrawdownsResponse) {
        submitDrawdownMutations.mutate({
          submissionForm,
        });
      }
    }, true, true,
  );

  const paymentsForm = useForm(
    paymentsInitialValues,
    paymentFormValidation,
    (values: FormikType) => {
      if (!values.payments?.length) throw Error('Invalid Payments');
      dispatch(initiatePayment({
        type: 'PAYMENTS',
        approverOwn: authorizedPaymentApproverOwn,
        paymentIds: values.payments!.map((p: FormikPaymentType) => p.id as string),
      }));
    },
  );

  useEffect(() => {
    if (selectedTrades?.length > 0) {
      const padEligibleApproved = selectedTrades.every((trade) => trade.padApproved && trade.padEligible);
      setPadEligibleAndApproved(padEligibleApproved);
    }
  }, [requestForm.values?.drawdowns, requestForm.values?.fundingMethod, selectedTrades]);

  const submitDrawdown = async ({
    submissionForm,
  }: {
        submissionForm: DrawdownSubmissionRequest,
        }) => {
    const response = await instance.post<DrawdownSubmissionRequest, any>(
      '/fx/batch-drawdowns/submit', {
        paymentIds: submissionForm.paymentIds,
        dynamicLinkingId: submissionForm.dynamicLinkingId || 'empty',
        totp: submissionForm.totp || '123456',
        approvalRequestId: submissionForm.approvalRequestId,
        softToken: submissionForm.softToken || true,
        drawdownIds: submissionForm.drawdownIds,
      },
    );

    return response.data;
  };

  const handleDrawdownMFA = async () => {
    if (paymentsForm?.values?.payments && paymentsForm?.values?.payments.length > 0) {
      // needs MFA with dynamic link id
      dispatch(initiateDrawdown({
        type: 'DRAWDOWN_BATCH',
        drawdownId: '',
        drawdownIds: validatedDrawdownsResponse?.validatedDrawdowns?.map((drawdown) => drawdown.id),
        paymentIds: paymentsForm?.values?.payments?.map((sp) => sp.id!) || [],
        approverOwn: authorizedPaymentApproverOwn,
        softToken: true,
        firstPartyDrawdown: false,
      }));
    } else if (requestForm.values.fundingMethod === DrawdownFundingMethod.PAD && !padEligibleAndApproved) {
      // needs MFA with TOTP
      dispatch(initiateDrawdown({
        type: 'DRAWDOWN_BATCH',
        drawdownId: '',
        drawdownIds: validatedDrawdownsResponse?.validatedDrawdowns?.map((drawdown) => drawdown.id),
        paymentIds: paymentsForm?.values?.payments?.map((sp) => sp.id!) || [],
        approverOwn: authorizedPaymentApproverOwn,
        softToken: true,
      }));
    } else {
      // No Need MFA, covering the following:
      // No payments with WIRED, ACCOUNT and PAD Authorised Trade
      drawdownSubmissionForm.setSubmitting(true);
      drawdownSubmissionForm.handleSubmit();
    }
  };

  useEffect(() => {
    drawdownSubmissionForm.setFieldValue('paymentIds',
      paymentsForm.values.payments!.map((p: FormikPaymentType) => p.id as string));
    drawdownSubmissionForm.validateForm(drawdownSubmissionForm.values);
  }, [paymentsForm.values.payments]);

  useEffect(() => {
    drawdownSubmissionForm.setFieldValue('drawdownIds',
      validatedDrawdownsResponse?.validatedDrawdowns?.map((drawdown) => drawdown.id));
    drawdownSubmissionForm.validateForm(drawdownSubmissionForm.values);
  }, [validatedDrawdownsResponse]);

  const onSubmissionSuccess = () => {
    sb.trigger(t('drawdowns_successfully_submitted'), 'success');
    logEvent({ action: 'Drawdowns Submitted Success', detail: { totalForwards: selectedTrades?.length, splitType: requestForm.values.splitType } });
    paymentsForm.resetForm();
    drawdownSubmissionForm.resetForm();
    requestForm.resetForm();

    setIsMassDrawdownFlowActive(false);
    setMassDrawdownDrawerOpen(false);
    setSelectedTrades([]);
    setSelectedTradeIds([]);
    setMassDrawdownValueDate(null);
  };

  const authyState = useSelector<TStore, TAuthyState>((state) => state.authy);

  useEffect(() => {
    if (
      authyState.type?.type === 'DRAWDOWN_BATCH'
      && authyState.status === 'SUCCESS'
      && !drawdownSubmissionForm.isSubmitting
    ) {
      onSubmissionSuccess();
    }
  }, [authyState.status, authyState.type]);

  const submitDrawdownMutations = useMutation(submitDrawdown, {
    onSuccess: () => {
      onSubmissionSuccess();
    },
    onError: (error: Error) => {
      sb.trigger(error ? `${t('error')}!: ${error}` : t('there_was_an_error_submitting_drawdowns'));
    },
  });

  useEffect(() => {
    mapTradeObjectWithSelectedIds(selectedTradeIds);
  }, [selectedTradeIds, trades]);

  useEffect(() => {
    setAdditionalTrades(availableForwardTrades.filter((trade) => !selectedTradeIds.includes(trade.id) && selectedTrades[0]?.currencyPair === trade.currencyPair));

    // initialise form when selected trades change & mass drawdown drawer opens
    // repopulate form when selected trades change
    if (requestForm.values.drawdowns.length !== selectedTradeIds.length && selectedTradeIds.length > 1 && massDrawdownDrawerOpen) {
      const drawdownValues = selectedTradeIds.map((tradeId) => {
        const index = requestForm.values.drawdowns.findIndex((item) => item.tradeId === tradeId);
        const tradeFromApi = availableForwardTrades.find((trade) => trade.id === tradeId);

        return {
          tradeId,
          valueDate: massDrawdownValueDate || '',
          fixCurrencyCode: requestForm.values.fixCurrencyCode || 'placeholder',
          amount: requestForm.values.drawdowns[index]?.amount || null,
          fundingMethod: requestForm.values.fundingMethod || DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
          sellCurrencyCode: selectedTrades[0]?.soldCurrencyCode || '',
          buyCurrencyCode: selectedTrades[0]?.buyCurrencyCode || '',
          rate: tradeFromApi?.rate || 0,
        };
      });

      const newTotal = drawdownValues.reduce((acc, curr) => acc.plus(curr.amount || 0), new Big(0)).toNumber();

      requestForm.setValues({
        drawdowns: drawdownValues,
        fixCurrencyCode: requestForm.values.fixCurrencyCode || 'placeholder',
        fundingMethod: requestForm.values.fundingMethod || DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
        splitType: requestForm.values.splitType || 'placeholder',
        evenSplitAmount: requestForm.values.evenSplitAmount || null,
        currencyPair: selectedTrades[0]?.currencyPair || '',
        totalDrawdownAmount: newTotal,
        weightedRate: requestForm.values.weightedRate || 0,
        valueDate: massDrawdownValueDate || '',
        sellCurrencyCode: selectedTrades[0]?.soldCurrencyCode || '',
        buyCurrencyCode: selectedTrades[0]?.buyCurrencyCode || '',
      });
    }
  }, [selectedTradeIds, massDrawdownDrawerOpen]);

  // initialise form when form has been reset
  useEffect(() => {
    if (requestForm.values.drawdowns.length < 2 && massDrawdownDrawerOpen) {
      const drawdownValues = selectedTradeIds.map((tradeId) => {
        const tradeFromApi = availableForwardTrades.find((trade) => trade.id === tradeId);

        return {
          tradeId,
          valueDate: massDrawdownValueDate || '',
          fixCurrencyCode: 'placeholder',
          amount: null,
          fundingMethod: DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
          sellCurrencyCode: selectedTrades[0]?.soldCurrencyCode || '',
          buyCurrencyCode: selectedTrades[0]?.buyCurrencyCode || '',
          rate: tradeFromApi?.rate || 0,
        };
      });

      requestForm.setValues({
        drawdowns: drawdownValues,
        fixCurrencyCode: 'placeholder',
        fundingMethod: DrawdownFundingMethod.CURRENCY_ACCOUNT_BALANCE,
        splitType: 'placeholder',
        evenSplitAmount: null,
        currencyPair: selectedTrades[0]?.currencyPair || '',
        totalDrawdownAmount: 0,
        weightedRate: 0,
        valueDate: massDrawdownValueDate || '',
        sellCurrencyCode: selectedTrades[0]?.soldCurrencyCode || '',
        buyCurrencyCode: selectedTrades[0]?.buyCurrencyCode || '',
      });
    }
  }, [requestForm.values.drawdowns.length]);

  useEffect(() => {
    if (requestForm.values?.splitType !== 'placeholder') {
      const initialVal = requestForm.values?.splitType === 'split-evenly' ? 0 : null;
      selectedTrades.forEach((trade, index) => {
        requestForm.setFieldValue(`drawdowns[${index}].amount`, initialVal);
      });
    }
    if (requestForm.values?.splitType === 'manually-enter') {
      requestForm.setFieldValue('evenSplitAmount', null);
    }
    requestForm.setFieldValue('totalDrawdownAmount', 0);
    setTradeErrors({});
  }, [requestForm.values?.splitType]);

  useEffect(() => {
    if (massDrawdownDrawerOpen && massDrawdownValueDate) {
      getAvailableForwardTrades().then((forwardTrades) => {
        setAvailableForwardTrades(forwardTrades);
        const filteredForwardTrades = forwardTrades.filter(
          (trade) => !selectedTradeIds.includes(trade.id) && selectedTrades[0].currencyPair === trade.currencyPair,
        );
        setAdditionalTrades(filteredForwardTrades);
      });
    }
  }, [massDrawdownDrawerOpen, massDrawdownValueDate]);

  // if the mass drawdown value date is after the value date of any selected trade, then unselect those trade
  useEffect(() => {
    if (massDrawdownValueDate) {
      const tradesAfterValueDate = selectedTrades.filter(
        (trade) => moment(moment(massDrawdownValueDate).format('YYYY-MM-DD')).isAfter(moment(trade.valueDate)),
      );
      setSelectedTradeIds(selectedTrades.filter((trade) => !tradesAfterValueDate.includes(trade)).map((trade) => trade.id));
      setSelectedTrades(selectedTrades.filter((trade) => !tradesAfterValueDate.includes(trade)));
    }
  }, [massDrawdownValueDate]);

  const handleSetSection = (_section: Section) => setSection(_section);

  useEffect(() => {
    if (!massDrawdownDrawerOpen) {
      setSection('Drawdown');
    }
  }, [massDrawdownDrawerOpen]);

  const calculateEvenSplitAmounts = (totalAmount: number, numberOfTrades: number): number[] => {
    if (!totalAmount) return Array(numberOfTrades).fill(0);

    const rawDividedAmount = totalAmount / numberOfTrades;
    const roundedDividedAmount = Number(rawDividedAmount.toFixed(2));

    // Calculate the last amount by subtracting the sum of other amounts from total
    const allAmountsExceptLast = Array(numberOfTrades - 1).fill(roundedDividedAmount);
    const sumOfAllAmountsExceptLast = allAmountsExceptLast.reduce((sum, amount) => sum + amount, 0);
    const lastAmount = Number((totalAmount - sumOfAllAmountsExceptLast).toFixed(2));

    return [...allAmountsExceptLast, lastAmount];
  };

  // update total and amounts when split type is split-evenly
  useEffect(() => {
    if (requestForm.values.splitType === 'split-evenly') {
      const totalEvenAmount = requestForm.values.evenSplitAmount ?? 0;
      const splitAmounts = calculateEvenSplitAmounts(totalEvenAmount, selectedTrades.length);

      selectedTrades.forEach((trade, index) => {
        requestForm.setFieldValue(`drawdowns[${index}].amount`, splitAmounts[index]);
        validateDrawdownAmount(trade.id, index, splitAmounts[index], requestForm.values.fixCurrencyCode);
      });

      requestForm.setFieldValue('totalDrawdownAmount', totalEvenAmount);
    }
  }, [requestForm.values?.evenSplitAmount, selectedTrades]);

  const calculateWeightedRate = () => {
    const convertedAmounts = selectedTrades?.map((trade) => {
      const drawdown = requestForm.values.drawdowns.find((d) => d.tradeId === trade.id);
      const amount = drawdown?.amount ?? 0;
      const rate = Number(trade.rate?.toFixed(4)) || 0;
      const result = requestForm.values.fixCurrencyCode === trade.soldCurrencyCode ? amount * rate : amount / rate;
      return Number.isNaN(result) ? 0 : result;
    });
    const totalConvertedAmount = convertedAmounts.reduce((sum, amount) => sum + amount, 0);
    const totalDDAmount = requestForm.values.totalDrawdownAmount || 0;
    // weighted rate is always total buy amount / total sold amount
    // if the fix currency code is sold, then totalConvertedAmount is total buy amount, totalDDAmount is total sold amount
    // if the fix currency code is buy, then totalDDAmount is total buy amount, totalConvertedAmount is total sold amount,
    const weightedRate = requestForm.values.fixCurrencyCode === selectedTrades[0].soldCurrencyCode
      ? totalConvertedAmount / totalDDAmount : totalDDAmount / totalConvertedAmount;
    return totalConvertedAmount > 0 ? weightedRate : 0;
  };

  useEffect(() => {
    if (requestForm.values.totalDrawdownAmount > 0) {
      const weightedRate = calculateWeightedRate();
      setCalculatedWeightedRate(weightedRate);
    } else {
      setCalculatedWeightedRate(0);
    }
  }, [requestForm.values?.totalDrawdownAmount, requestForm.values.fixCurrencyCode, selectedTrades]);

  useEffect(() => {
    requestForm.setFieldValue('weightedRate', calculatedWeightedRate);
  }, [calculatedWeightedRate]);

  return {
    massDrawdownDrawerOpen,
    setMassDrawdownDrawerOpen,
    drawdownSubmissionForm,
    handleMassDrawdownClick,
    isMassDrawdownFlowActive,
    selectedTrades,
    setSelectedTrades,
    handleCancelMassDrawdown,
    massDrawdownValueDate,
    setMassDrawdownValueDate,
    selectedTradeIds,
    setSelectedTradeIds,
    setTrades,
    requestForm,
    handleValueDateChange,
    handleSplitChange,
    handleCurrencyChange,
    handleFormSubmit,
    additionalTrades,
    handleAddAdditionalTrade,
    validateDrawdownMutations,
    handleDrawdownMFA,
    validateDrawdowns,
    submitDrawdownMutations,
    submitDrawdown,
    section,
    handleSetSection,
    paymentsForm,
    validatedDrawdownsResponse,
    handleAmountChange,
    confirmationModalType,
    setConfirmationModalType,
    openConfirmation,
    setOpenConfirmation,
    handleConfirmationModal,
    setTradeIdToRemove,
    padEligibleAndApproved,
    defaultSellCurrencyAccount,
    defaultBuyCurrencyAccount,
    accountConfigQuery,
    calculatedWeightedRate,
    tradeErrors,
  };
};

export default useMassDrawdown;
